/**
 * Browser Geolocation API wrapper.
 *
 * Resolves the permission/grant outcome instead of throwing, so the caller can
 * record both a granted fix and a refusal. `permission_status` is always one of
 * the four values the backend `/v1/geo-observation` endpoint accepts:
 * granted | denied | unavailable | prompt_dismissed.
 *
 * `prompt_dismissed` is never produced here (the browser API has no
 * "dismissed" outcome). It is reserved for the caller when the user dismisses
 * the pre-permission explainer without choosing.
 */

function unavailable() {
    return { permission_status: 'unavailable', lat: null, lon: null, accuracy: null };
}

/**
 * Request the device position. Never throws.
 * @param {number} timeout
 * @returns {Promise<{permission_status: string, lat: ?number, lon: ?number, accuracy: ?number}>}
 */
export async function requestBrowserGeoObservation(timeout = 10000) {
    if (!('geolocation' in navigator)) {
        return unavailable();
    }

    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            (position) => resolve({
                permission_status: 'granted',
                lat: position.coords.latitude,
                lon: position.coords.longitude,
                accuracy: position.coords.accuracy != null
                    ? Math.round(position.coords.accuracy)
                    : null
            }),
            (error) => resolve(mapGeolocationError(error)),
            { timeout, maximumAge: 300000, enableHighAccuracy: false }
        );
    });
}

/**
 * Map a GeolocationPositionError onto a backend permission_status.
 * code 1 PERMISSION_DENIED -> denied; code 2 POSITION_UNAVAILABLE and
 * code 3 TIMEOUT -> unavailable (the backend has no separate timeout state).
 */
function mapGeolocationError(error) {
    const status = (error && error.code === 1) ? 'denied' : 'unavailable';
    return { permission_status: status, lat: null, lon: null, accuracy: null };
}
